import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Wallet, TrendingUp, Clock, AlertCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import api from '../lib/api';
import { Link } from 'react-router-dom';

const KpiCard = ({ icon: Icon, label, value, sub, cls, testId }) => (
    <div className={`p-4 rounded-xl border ${cls}`} data-testid={testId}>
        <div className="flex items-start justify-between gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">
                {label}
            </span>
            <Icon className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="text-2xl font-bold font-mono">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
);

const FinanceSnapshot = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await api.get('/dashboard/finance');
                setData(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }
    if (!data) return null;

    const m = data.metrics || {};

    return (
        <Card className="bg-card/50 border-border/40" data-testid="finance-snapshot">
            <CardContent className="p-5">
                <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                            <Wallet className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="font-heading font-semibold">Finance Snapshot</h2>
                            <p className="text-xs text-muted-foreground">All-time Revenue, Paid, and Pending to labels</p>
                        </div>
                    </div>
                    <Button asChild variant="outline" size="sm" className="gap-1">
                        <Link to="/admin/finance" data-testid="open-finance-dashboard">
                            Open Finance Dashboard <ArrowRight className="w-4 h-4" />
                        </Link>
                    </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiCard
                        icon={TrendingUp}
                        label="Gross Revenue"
                        value={formatCurrency(m.total_revenue?.inr || 0, 'INR')}
                        sub={`${(m.total_revenue?.records || 0).toLocaleString()} records`}
                        cls="bg-green-500/5 border-green-500/20"
                        testId="snapshot-revenue"
                    />
                    <KpiCard
                        icon={CheckCircle}
                        label="Paid to Labels"
                        value={formatCurrency(m.total_paid?.inr || 0, 'INR')}
                        sub={`${m.total_paid?.count || 0} invoice(s)`}
                        cls="bg-blue-500/5 border-blue-500/20"
                        testId="snapshot-paid"
                    />
                    <KpiCard
                        icon={Clock}
                        label="Pending to Labels"
                        value={formatCurrency(m.total_pending?.inr || 0, 'INR')}
                        sub={`Earned ${formatCurrency(m.total_pending?.earned_net_inr || 0, 'INR')}`}
                        cls="bg-yellow-500/5 border-yellow-500/20"
                        testId="snapshot-pending"
                    />
                    <KpiCard
                        icon={AlertCircle}
                        label="Not Yet Invoiced"
                        value={formatCurrency(m.total_pending?.unbilled_inr || 0, 'INR')}
                        sub={`Invoiced ${formatCurrency(m.total_pending?.invoiced_inr || 0, 'INR')} • ${m.total_pending?.invoiced_count || 0} in workflow`}
                        cls="bg-orange-500/5 border-orange-500/20"
                        testId="snapshot-unbilled"
                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default FinanceSnapshot;
